import type {PaymentDetails} from 'src/types/referrer-portal';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {getBankInputFields} from 'src/utils/referrer-portal';
import {
  Card,
  CardHeader,
  CardContent,
  RadioButton,
  Input,
  Icon,
  SubTitleMedium,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/index';
import Paypal from 'src/images/designSystems2021/paypal.svg';

import css from 'src/components/referrer-portal/earnings/earnings.css';


const PaymentCard = ({
  paymentData,
  handleOnChange,
  errors,
  footer,
}: {
  paymentData: PaymentDetails;
  handleOnChange: (field: string, value: string) => unknown;
  errors: Map<string, string>;
  footer?: React.ReactNode;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const BANK_INPUT_FIELDS = getBankInputFields(t);
  const inputType = (field: string) => {
    switch (field) {
      case 'routing_number':
      case 'account_number':
      case 'swift_code':
        return 'number';
      default:
        return 'text';
    }
  };

  return (
    <div className={css.flexColumn}>
      <PaymentRadioSelect
        value={paymentData.method}
        handleOnChange={handleOnChange}
      />

      {paymentData.method === 'paypal' ? (
        <Card classNames={{wrapper: css.paymentFormCard}}>
          <CardHeader>{t('PayPal_Details', 'PayPal Details')}</CardHeader>
          <CardContent className={css.flexColumn}>
            <Input
              required={true}
              label={t('PayPal_ID', 'PayPal ID')}
              value={paymentData.details['paypal_id']}
              placeholder={t('Enter_id', 'Enter id')}
              onChange={(event) =>
                handleOnChange('paypal_id', event.target.value)
              }
            />
            {errors.has('paypal_id') && (
              <BodySmall color="danger">{errors.get('paypal_id')}</BodySmall>
            )}
          </CardContent>
          {footer}
        </Card>
      ) : (
        <Card classNames={{wrapper: css.paymentFormCard}}>
          <CardHeader>{t('Bank_Details', 'Bank Details')}</CardHeader>
          <CardContent className={css.bankDetails}>
            {BANK_INPUT_FIELDS.map((field) => (
              <div>
                <Input
                  required={true}
                  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ name: string; bank_name: string; account_number: string; routing_number: string; swift_code: string; paypal_id: string; }'.
                  value={paymentData.details[field.key]}
                  type={inputType(field.key)}
                  onChange={(event) =>
                    handleOnChange(field.key, event.target.value)
                  }
                  label={field.label}
                />
                {errors.has(field.key) && (
                  <BodySmall color="danger">{errors.get(field.key)}</BodySmall>
                )}
              </div>
            ))}
          </CardContent>
          {footer}
        </Card>
      )}
    </div>
  );
};

export default PaymentCard;

type PaymentRadioSelectProps = {
  value: string;
  handleOnChange?: (fieldName: string, value: string) => unknown;
};

export const PaymentRadioSelect = ({
  value,
  handleOnChange,
}: PaymentRadioSelectProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <div className={css.paymentRadioSelect}>
      <Card classNames={{wrapper: css.paymentTab}}>
        <div className={css.paymentTab}>
          <Paypal /> <SubTitleMedium>{t('PayPal', 'PayPal')}</SubTitleMedium>
        </div>
        <RadioButton
          disabled={!handleOnChange}
          onChange={(value) => handleOnChange?.('method', value)}
          selectedValue={value}
          value="paypal"
        />
      </Card>
      <Card classNames={{wrapper: css.paymentTab}}>
        <div className={css.paymentTab}>
          <Icon name="bank" />{' '}
          <SubTitleMedium>{t('Bank', 'Bank')}</SubTitleMedium>
        </div>
        <RadioButton
          disabled={!handleOnChange}
          onChange={(value) => handleOnChange?.('method', value)}
          selectedValue={value}
          value="bank"
        />
      </Card>
    </div>
  );
};
